import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Button, LinkButton } from "../../../../components/Button";
import { PlusTag } from "../../../../components/PlusTag";
import { useQueryUrl } from "../../../../hooks/QueryUrl";
import ChevronLeftIcon from "../../../../icons/ChevronLeft";
import { Site } from "../../../../model/site";
import { formatDate as formatLocaleDate } from "../../../../services/i18ln/format";
import { FlowLayout } from "../../components/FlowLayout";
import { formatBRL } from "../../pricing";
import { useFlowEditingSite } from "../../useFlowEditingSite";
import { isPlus } from "../../waitForPlus";
import { BoletoIcon, PixIcon } from "../Checkout/icons";
import { TopLinks } from "../Features/styles";
import { formatBoletoLine, loadPendingPayment } from "./pendingPayment";
import { Card, Code, Container, Content, QrCode, Status, Steps } from "./styles";

// The Pix code really lasts 3 days (a later payment still turns the Keepo PLUS): the countdown is shorter on
// purpose, to hurry the person
const PIX_MINUTES = 30;
// While the Pix screen is open, the payment is checked this often
const PIX_POLL_MS = 4000;

const useCopy = () => {
  const [copied, setCopied] = useState(false);
  const copy = (text: string) => {
    navigator.clipboard?.writeText(text).catch(() => undefined);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };
  return { copied, copy };
};

/** After choosing Pix or boleto: what to pay, and the wait for it (the PLUS turns on once it's paid). */
type Props = {
  site?: Site;
  setSite: (site: Site) => void;
};

export const PaymentPendingPage = ({ site, setSite }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryUrl = useQueryUrl();
  const prefix = "pages.subscription.pending";

  const paymentId = queryUrl.get("payment") || "";
  const [payment] = useState(() => loadPendingPayment(paymentId));
  const method = payment?.method || "pix";
  const amount = payment ? formatBRL(payment.amount / 100) : "";
  const siteId = payment?.siteId || queryUrl.get("site");
  const editorFlow = queryUrl.get("flow");
  const isEditorFlow = editorFlow === "create" || editorFlow === "edit";
  // The Keepo being created stays in the side preview, even after a reload
  useFlowEditingSite(isEditorFlow, siteId, site, setSite);

  const { copied, copy } = useCopy();
  const [secondsLeft, setSecondsLeft] = useState(PIX_MINUTES * 60);
  const [checkState, setCheckState] = useState<"idle" | "checking" | "notYet">("idle");
  const dueDate = payment?.boleto?.expiresAt ? formatLocaleDate(payment.boleto.expiresAt, i18n.language) : "";

  const checkoutParams = new URLSearchParams({ plan: queryUrl.get("plan") || "ANNUALLY" });
  if (siteId) checkoutParams.set("site", siteId);
  if (editorFlow) checkoutParams.set("flow", editorFlow);
  const checkoutUrl = `/subscription/checkout?${checkoutParams}`;

  // The Keepo is PLUS (Pix paid, or boleto generated): the success screen takes over (in the creation flow it
  // goes on to publishing)
  const goToSuccess = useCallback(() => {
    const params = new URLSearchParams({ siteId: siteId as string });
    if (editorFlow) params.set("flow", editorFlow);
    // A boleto isn't paid yet: publishing says so
    if (method === "boleto") params.set("method", "boleto");
    navigate(`/subscription/success?${params}`, { replace: true });
  }, [editorFlow, method, navigate, siteId]);

  // Opened without the payment it should show (e.g. another tab): back to choosing how to pay
  useEffect(() => {
    if (!payment) {
      navigate(checkoutUrl, { replace: true });
    }
    // Only on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Pix code expires: a countdown, then a new one
  useEffect(() => {
    if (method !== "pix" || secondsLeft <= 0) {
      return;
    }
    const timer = window.setTimeout(() => setSecondsLeft(secondsLeft - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [method, secondsLeft]);

  // Pix: the screen moves on by itself once the payment arrives (Stripe → API webhook → PLUS)
  useEffect(() => {
    if (method !== "pix" || !siteId) {
      return;
    }
    let cancelled = false;
    const timer = window.setInterval(async () => {
      if (!cancelled && (await isPlus(siteId))) {
        cancelled = true;
        goToSuccess();
      }
    }, PIX_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [goToSuccess, method, siteId]);

  const isExpired = method === "pix" && secondsLeft <= 0;
  const countdown = [Math.floor(secondsLeft / 60), secondsLeft % 60]
    .map(part => String(part).padStart(2, "0"))
    .join(":");

  // "Já paguei": checks right away instead of waiting for the next round
  const checkPayment = async () => {
    setCheckState("checking");
    if (siteId && (await isPlus(siteId))) {
      goToSuccess();
      return;
    }
    setCheckState("notYet");
  };

  if (!payment) {
    return null;
  }

  const content = (
    <>
      <header>
        <span className="method-icon">
          {method === "pix" ? <PixIcon width={28} height={28} /> : <BoletoIcon width={28} height={28} />}
        </span>
        <h1>{t(`${prefix}.${method}.title`)}</h1>
        <p>{t(`${prefix}.${method}.subtitle`)}</p>
      </header>

      <Card>
        <div className="amount">
          <PlusTag>✦ Keepo Plus</PlusTag>
          <b>{amount}</b>
        </div>

        {method === "pix" ? (
          <>
            <QrCode $expired={isExpired}>
              {payment.pix?.qrImageUrl && <img src={payment.pix.qrImageUrl} alt="QR code Pix" />}
              {isExpired && <span className="expired">{t(`${prefix}.pix.expired`)}</span>}
            </QrCode>

            {isExpired ? (
              <button type="button" className="primary" onClick={() => setSecondsLeft(PIX_MINUTES * 60)}>
                {t(`${prefix}.pix.newCode`)}
              </button>
            ) : (
              <>
                <p className="timer">{t(`${prefix}.pix.expiresIn`, { time: countdown })}</p>
                <Code>
                  <span className="label">{t(`${prefix}.pix.copyPaste`)}</span>
                  <code>{payment.pix?.code}</code>
                </Code>
                <button type="button" className="primary" onClick={() => copy(payment.pix?.code || "")}>
                  {copied ? t(`${prefix}.copied`) : t(`${prefix}.pix.copy`)}
                </button>
              </>
            )}
          </>
        ) : (
          <>
            <p className="due">{t(`${prefix}.boleto.dueOn`, { date: dueDate })}</p>
            <Code>
              <span className="label">{t(`${prefix}.boleto.line`)}</span>
              <code className="boleto-line">{formatBoletoLine(payment.boleto?.number || "")}</code>
            </Code>
            <div className="actions">
              <button
                type="button"
                className="primary"
                onClick={() => copy((payment.boleto?.number || "").replace(/\D/g, ""))}>
                {copied ? t(`${prefix}.copied`) : t(`${prefix}.boleto.copy`)}
              </button>
              <a
                className="secondary"
                href={payment.boleto?.pdfUrl || payment.boleto?.hostedUrl}
                target="_blank"
                rel="noreferrer">
                {t(`${prefix}.boleto.download`)}
              </a>
            </div>
          </>
        )}
      </Card>

      <Steps>
        {[1, 2, 3].map(step => (
          <li key={step}>
            <span className="number">{step}</span>
            {t(`${prefix}.${method}.steps.${step}`)}
          </li>
        ))}
      </Steps>

      {method === "pix" ? (
        <Status>
          <span className="dot" aria-hidden="true" />
          <span className="text">
            <b>{t(`${prefix}.pix.waiting`)}</b>
            <span>{checkState === "notYet" ? t(`${prefix}.pix.notYet`) : t(`${prefix}.pix.waitingDescription`)}</span>
          </span>
          {!isEditorFlow && (
            <button type="button" disabled={checkState === "checking"} onClick={checkPayment}>
              {checkState === "checking" ? t(`${prefix}.pix.checking`) : t(`${prefix}.pix.paid`)}
            </button>
          )}
        </Status>
      ) : (
        <Status $calm>
          <span className="dot" aria-hidden="true" />
          <span className="text">
            <b>{t(`${prefix}.boleto.waiting`)}</b>
            <span>{t(`${prefix}.boleto.waitingDescription`)}</span>
          </span>
          {!isEditorFlow && (
            <button type="button" onClick={goToSuccess}>
              {t(`${prefix}.boleto.done`)}
            </button>
          )}
        </Status>
      )}
    </>
  );

  // A step of creating/editing a Keepo: the flow's look, with the Keepo's preview on the side
  if (isEditorFlow) {
    return (
      <FlowLayout
        title={t("pages.subscription.flow.checkoutTitle")}
        back={{ label: t(`${prefix}.changeMethod`), to: checkoutUrl }}
        bar={
          <>
            <LinkButton onClick={() => navigate(checkoutUrl)}>{t(`${prefix}.changeMethod`)}</LinkButton>
            {method === "pix" ? (
              <Button
                type="INFO"
                size="LG"
                $loading={checkState === "checking"}
                onClick={() => checkState !== "checking" && checkPayment()}>
                {t(`${prefix}.pix.paid`)}
              </Button>
            ) : (
              <Button type="INFO" size="LG" onClick={goToSuccess}>
                {t(`${prefix}.boleto.done`)}
              </Button>
            )}
          </>
        }>
        <Content $flow>{content}</Content>
      </FlowLayout>
    );
  }

  return (
    <Container>
      <TopLinks>
        <button type="button" onClick={() => navigate(checkoutUrl)}>
          <ChevronLeftIcon width={10} height={10} color="currentColor" />
          {t(`${prefix}.changeMethod`)}
        </button>
      </TopLinks>

      <Content>{content}</Content>
    </Container>
  );
};
